import type { ApiClient } from '../http/apiClient';
import type { IdentityProvider } from '../../auth/identityProvider';
import { buildMetaInfo } from '../model/metaInfo';
import type {
  DescribeExecutionRequest,
  DescribeExecutionResponse,
} from '../model/types';

/**
 * Orchestrator client — introspection over Step Functions executions started by
 * long-running backend workflows (e.g. knowledge-base provisioning). One method
 * per deployed orchestrator operation (DescribeExecution). Paths mirror
 * model/orchestrator/operations.smithy exactly.
 *
 * Calls {@link ApiClient} only. Pure TS — no React.
 *
 * metaInfo: jobCategory DOCUMENT_MANAGEMENT and the KNOWN-VALID contextual
 * requestType FETCH_META_INFO (describing one execution is a single-item read).
 * The backend RequestType enum has no orchestrator-specific value and rejects
 * unknown enums, so we reuse an accepted value; requestType is contextual only
 * and does not affect behavior.
 */

const PATHS = {
  describeExecution: '/orchestrator/describe-execution',
} as const;

export interface DescribeExecutionArgs {
  executionArn: string;
}

/** Normalized result of DescribeExecution. */
export interface ExecutionDescription {
  /** SFN execution status: RUNNING | SUCCEEDED | FAILED | TIMED_OUT | ABORTED. */
  status: string;
  /** Execution output payload (a JSON string), or '' when not reported. */
  output: string;
}

export class OrchestratorApi {
  constructor(
    private readonly apiClient: ApiClient,
    private readonly identityProvider: IdentityProvider,
  ) {}

  /**
   * Describes a Step Functions execution, returning its status and output.
   * Callers poll this until `status` is a terminal value (SUCCEEDED / FAILED /
   * TIMED_OUT / ABORTED). The `output` JSON string also surfaces the per-step
   * provisioning status.
   */
  async describeExecution(
    args: DescribeExecutionArgs,
  ): Promise<ExecutionDescription> {
    const metaInfo = buildMetaInfo({
      identity: await this.identityProvider.getIdentity(),
      jobName: 'DescribeExecution',
      jobCategory: 'DOCUMENT_MANAGEMENT',
      requestType: 'FETCH_META_INFO',
    });
    const res = await this.apiClient.post<
      DescribeExecutionRequest,
      DescribeExecutionResponse
    >(PATHS.describeExecution, {
      metaInfo,
      executionArn: args.executionArn,
    });
    return {
      status: res.status ?? '',
      output: res.output ?? '',
    };
  }
}
